import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { truckingBillAPI } from '../../api/waybillAPI';

const PHP = (n) =>
  new Intl.NumberFormat('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-PH', { month: '2-digit', day: '2-digit', year: 'numeric' }) : '—';

const fmtLong = (dateStr) =>
  new Date(dateStr + 'T00:00:00').toLocaleDateString('en-PH', { month: 'long', day: 'numeric', year: 'numeric' });

export default function DailyBillPrintPage() {
  const [searchParams] = useSearchParams();
  const dateFrom = searchParams.get('dateFrom') || new Date().toISOString().split('T')[0];
  const dateTo = searchParams.get('dateTo') || dateFrom;
  const billType = searchParams.get('billType') || '';

  const [bills, setBills] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    truckingBillAPI.getDailyBill({ dateFrom, dateTo, billType })
      .then((res) => {
        const d = res.data || {};
        setBills(Array.isArray(d.data) ? d.data : []);
        setSummary(d.summary || {});
        setLoading(false);
        setTimeout(() => window.print(), 400);
      })
      .catch(() => setLoading(false));
  }, []); // eslint-disable-line

  useEffect(() => {
    const label = dateFrom === dateTo ? fmtLong(dateFrom) : `${dateFrom} to ${dateTo}`;
    document.title = `Daily Billing Report — ${label}`;
  }, [dateFrom, dateTo]);

  const dateLabel = dateFrom === dateTo
    ? fmtLong(dateFrom)
    : `${fmtLong(dateFrom)} – ${fmtLong(dateTo)}`;

  if (loading) return <p style={{ padding: 40 }}>Loading report…</p>;

  const totalSales    = summary.totalSales    || 0;
  const totalReceived = summary.totalReceived || 0;
  const totalUnpaid   = summary.totalUnpaid   || 0;

  return (
    <>
      <style>{`
        body { font-family: Arial, sans-serif; font-size: 10pt; margin: 0; }
        @page { size: A4 landscape; margin: 12mm; }
        @media print { .no-print { display: none !important; } }
        .header { text-align: center; margin-bottom: 8px; }
        h1 { font-size: 14pt; margin: 0; }
        h2 { font-size: 11pt; margin: 2px 0; font-weight: normal; }
        table { width: 100%; border-collapse: collapse; font-size: 9.5pt; }
        th { background: #f0f0f0; border: 1px solid #ddd; padding: 4px 8px; text-align: left; font-size: 9pt; }
        td { border: 1px solid #ddd; padding: 4px 8px; }
        tr:nth-child(even) { background: #fafafa; }
        .badge-ok  { color: #166534; background: #dcfce7; padding: 1px 6px; border-radius: 10px; font-size: 8.5pt; }
        .badge-no  { color: #374151; background: #f3f4f6; padding: 1px 6px; border-radius: 10px; font-size: 8.5pt; }
        .summary-cards { display: flex; gap: 20px; margin-bottom: 10px; }
        .card { border: 1px solid #ddd; border-radius: 6px; padding: 6px 14px; min-width: 140px; }
        .card p { margin: 0; font-size: 8.5pt; color: #555; }
        .card strong { font-size: 12pt; }
        .tfoot-row { background: #f0f0f0; font-weight: bold; }
        .text-right { text-align: right; }
      `}</style>

      <div className="header">
        <h1>Daily Billing Report</h1>
        <h2>{dateLabel}</h2>
      </div>

      <div className="summary-cards">
        <div className="card">
          <p>Total Sales</p>
          <strong>₱{PHP(totalSales)}</strong><br/>
          <span style={{ fontSize: '8pt', color: '#777' }}>{bills.length} bills</span>
        </div>
        <div className="card">
          <p>Received</p>
          <strong style={{ color: '#166534' }}>₱{PHP(totalReceived)}</strong><br/>
          <span style={{ fontSize: '8pt', color: '#777' }}>{bills.filter(b => b.IsReceived).length} bills</span>
        </div>
        <div className="card">
          <p>Unpaid</p>
          <strong style={{ color: '#be123c' }}>₱{PHP(totalUnpaid)}</strong><br/>
          <span style={{ fontSize: '8pt', color: '#777' }}>{bills.filter(b => !b.IsReceived).length} bills</span>
        </div>
      </div>

      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>Bill No.</th>
            <th>Bill Date</th>
            <th>Due Date</th>
            <th>Trucking Name</th>
            <th>Carrier</th>
            <th>Received</th>
            <th className="text-right">Total Amount</th>
          </tr>
        </thead>
        <tbody>
          {bills.length === 0 ? (
            <tr><td colSpan={8} style={{ textAlign: 'center', padding: 16 }}>No bills found.</td></tr>
          ) : bills.map((b, i) => {
            const truckingName = b.TruckingId?.CompanyName || b.TruckingName || '—';
            return (
              <tr key={b._id}>
                <td>{i + 1}</td>
                <td><strong>{b.BillName}</strong></td>
                <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(b.BillDate)}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(b.BillDueDate)}</td>
                <td>{truckingName}</td>
                <td>{b.CarrierName || '—'}</td>
                <td>{b.IsReceived ? <span className="badge-ok">Received</span> : <span className="badge-no">Unpaid</span>}</td>
                <td className="text-right">₱{PHP(b.totalAmount)}</td>
              </tr>
            );
          })}
          {bills.length > 0 && (
            <tr className="tfoot-row">
              <td colSpan={7} className="text-right">Grand Total</td>
              <td className="text-right">₱{PHP(totalSales)}</td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="no-print" style={{ marginTop: 16 }}>
        <button onClick={() => window.print()} style={{ padding: '6px 16px' }}>Print</button>
      </div>
    </>
  );
}
